"use client";

import { c as _c } from "react/compiler-runtime";
import { type PointerEvent, type RefObject, useEffect } from "react";
import { canAnimatePointer, loadGsap, shouldReduceMotion } from "@/lib/gsap";
type PageHeroRefs = {
  sectionRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  imageWrapRef: RefObject<HTMLDivElement | null>;
  imageRef: RefObject<HTMLImageElement | null>;
  glowRef: RefObject<HTMLDivElement | null>;
  dotsRef: RefObject<HTMLImageElement | null>;
  textRightRef: RefObject<HTMLDivElement | null>;
};
export function usePageHeroAnimation(t0) {
  const $ = _c(20);
  const {
    sectionRef,
    contentRef,
    imageWrapRef,
    imageRef,
    glowRef,
    dotsRef,
    textRightRef
  } = t0;
  let t1;
  let t2;
  if ($[0] !== contentRef || $[1] !== dotsRef || $[2] !== glowRef || $[3] !== imageWrapRef || $[4] !== sectionRef || $[5] !== textRightRef) {
    t1 = () => {
      if (shouldReduceMotion()) {
        return;
      }
      let cleanup;
      let cancelled = false;
      const setupAnimation = async function setupAnimation() {
        const gsap = await loadGsap();
        if (cancelled) {
          return;
        }
        const ctx = gsap.context(() => {
          const content = contentRef.current;
          const tag = content?.querySelector(".hero-webpart-tag");
          const title = content?.querySelector(".hero-webpart-title");
          const description = content?.querySelector(".hero-webpart-description");
          const actions = content?.querySelector(".hero-webpart-actions");
          const imageWrap = imageWrapRef.current;
          const glow = glowRef.current;
          const dots = dotsRef.current;
          const textRight = textRightRef.current;
          if (!tag || !title) {
            return;
          }
          const timeline = gsap.timeline({
            delay: 0.5,
            defaults: {
              ease: "power4.out"
            }
          });
          timeline.from(tag, {
            x: -80,
            opacity: 0,
            duration: 0.8
          }).from(title, {
            x: -120,
            opacity: 0,
            filter: "blur(10px)",
            duration: 1,
            clearProps: "filter"
          }, "-=0.45");
          if (description) {
            timeline.from(description, {
              x: -80,
              opacity: 0,
              duration: 0.8
            }, "-=0.55");
          }
          if (imageWrap) {
            timeline.fromTo(imageWrap, {
              x: 180,
              y: 35,
              opacity: 0,
              scale: 0.88,
              rotateY: -18,
              rotateZ: 3,
              transformPerspective: 1200
            }, {
              x: 0,
              y: 0,
              opacity: 1,
              scale: 1,
              rotateY: 0,
              rotateZ: 0,
              duration: 1.25
            }, description ? "-=0.9" : "-=0.55");
          }
          if (glow) {
            timeline.from(glow, {
              opacity: 0,
              scale: 0.65,
              duration: 1.3
            }, "-=1");
          }
          if (dots) {
            timeline.from(dots, {
              x: 80,
              opacity: 0,
              duration: 1.1
            }, "-=1");
          }
          if (textRight) {
            timeline.fromTo(textRight, {
              x: 140,
              y: 24,
              opacity: 0,
              scale: 0.94,
              rotateY: -14,
              transformPerspective: 1200
            }, {
              x: 0,
              y: 0,
              opacity: 1,
              scale: 1,
              rotateY: 0,
              duration: 1.1
            }, imageWrap ? "-=0.95" : "-=0.65");
          }
          if (actions) {
            timeline.from(actions, {
              x: -80,
              opacity: 0,
              duration: 0.8
            }, "-=1.05");
          }
        }, sectionRef);
        cleanup = () => ctx.revert();
      };
      setupAnimation();
      return () => {
        cancelled = true;
        cleanup?.();
      };
    };
    t2 = [sectionRef, contentRef, imageWrapRef, glowRef, dotsRef, textRightRef];
    $[0] = contentRef;
    $[1] = dotsRef;
    $[2] = glowRef;
    $[3] = imageWrapRef;
    $[4] = sectionRef;
    $[5] = textRightRef;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  if ($[8] !== dotsRef || $[9] !== glowRef || $[10] !== imageRef || $[11] !== sectionRef) {
    t3 = async function handlePointerMove(event) {
      if (!canAnimatePointer()) {
        return;
      }
      const section = sectionRef.current;
      const image = imageRef.current;
      const glow_0 = glowRef.current;
      const dots_0 = dotsRef.current;
      if (!section || !image || !glow_0 || !dots_0) {
        return;
      }
      const rect = section.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      const gsap_0 = await loadGsap();
      gsap_0.to(image, {
        x: x * 28,
        y: y * 22,
        rotateY: x * 7,
        rotateX: y * -7,
        duration: 0.8,
        ease: "power3.out"
      });
      gsap_0.to(glow_0, {
        x: x * 70,
        y: y * 45,
        duration: 1,
        ease: "power3.out"
      });
      gsap_0.to(dots_0, {
        x: x * -35,
        y: y * -25,
        duration: 1.1,
        ease: "power3.out"
      });
    };
    $[8] = dotsRef;
    $[9] = glowRef;
    $[10] = imageRef;
    $[11] = sectionRef;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handlePointerMove = t3;
  let t4;
  if ($[13] !== dotsRef || $[14] !== glowRef || $[15] !== imageRef) {
    t4 = async function handlePointerLeave() {
      if (!canAnimatePointer()) {
        return;
      }
      const targets = [imageRef.current, glowRef.current, dotsRef.current].filter(_temp);
      if (!targets.length) {
        return;
      }
      const gsap_1 = await loadGsap();
      gsap_1.to(targets, {
        x: 0,
        y: 0,
        rotateX: 0,
        rotateY: 0,
        duration: 1,
        ease: "power3.out"
      });
    };
    $[13] = dotsRef;
    $[14] = glowRef;
    $[15] = imageRef;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handlePointerLeave = t4;
  let t5;
  if ($[17] !== handlePointerLeave || $[18] !== handlePointerMove) {
    t5 = {
      handlePointerMove,
      handlePointerLeave
    };
    $[17] = handlePointerLeave;
    $[18] = handlePointerMove;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
function _temp(target) {
  return target !== null;
}